import { fabric } from "fabric";
import type { ITextboxOptions } from "fabric/fabric-impl";
import { extractHttpUrls } from "../../helpers/url";
import {
  BorderedGroup,
  BorderedImage,
  BorderedTextBox,
} from "../elements/bordered-objects";
import { defaults } from "../state/defaults";
import type {
  FieldAddress,
  FieldItem,
  TextareaFieldItem,
} from "../state/types";
import {
  isValidAddress,
  makeGoogleMapsLink,
  makeGoogleMapsStaticMapLink,
} from "../state/utils";
import { makeGroupClipPath } from "./fabric-file-renderer";
import type { RenderContext } from "./fabric-renderer";
import { calculateTopWithPageBreak } from "./fabric-renderer-utils";

export type FieldElement = BorderedGroup | BorderedTextBox;

export function renderField(
  context: RenderContext,
  item: FieldItem,
  element: FieldElement | undefined,
): fabric.Object {
  const renderedElement = renderElement(context, item, element);
  const { updated, newTop } = calculateTopWithPageBreak(
    renderedElement,
    context.dimensions,
  );

  if (updated) {
    renderedElement.setOptions({ top: newTop });
  }

  return renderedElement;
}

function renderElement(
  context: RenderContext,
  item: FieldItem,
  element: FieldElement | undefined,
): FieldElement {
  const fieldValue = context.record?.[item.field_slug] ?? null;
  const fieldValueString = context.data[item.field_slug] ?? "";

  let url: string | undefined;
  const isAddressField = !!item.addressType;

  if (isAddressField) {
    const hasValidAddress =
      fieldValue && isValidAddress(fieldValue as FieldAddress);
    const isAddressMap = item.addressType === "map";

    if (hasValidAddress) {
      const isAddressWithLink = item.addressType === "link" || isAddressMap;
      if (isAddressWithLink) {
        url = makeGoogleMapsLink(fieldValueString);
      }

      if (isAddressMap) {
        return renderAddressMap(element as BorderedGroup, {
          item,
          fieldContent: fieldValueString,
          url,
        });
      }
    } else if (isAddressMap) {
      // Render an empty group if the address is invalid and address type is map
      return renderAddressMap(element as FieldElement, {
        item,
        fieldContent: "",
      });
    }
  }

  // Fields whose value contains links (e.g. linkfield) get working pdf
  // annotations even when the visible text wraps across several lines.
  let urls: string[] | undefined;
  if (!url) {
    urls = extractHttpUrls(fieldValueString);
    if (urls.length === 1) {
      url = urls[0];
    }
  }

  return renderTextField(element, {
    item,
    fieldContent: fieldValueString,
    url,
    urls,
  });
}

interface RenderParams {
  item: FieldItem | TextareaFieldItem;
  fieldContent: string;
  url?: string;
  urls?: string[];
}

export function renderTextField(
  element: FieldElement | BorderedTextBox | undefined,
  { item, fieldContent, url, urls }: RenderParams,
): BorderedTextBox {
  const options: ITextboxOptions = {
    left: item.left,
    top: item.top,
    width: item.width,
    height: item.height,
    angle: item.angle,
    fontSize: item.fontSize,
    fontFamily: item.fontFamily,
    fontWeight: item.fontWeight,
    textAlign: item.textAlign,
    lineHeight: item.lineHeight,
    fill: item.fill ?? defaults.textColor,
  };

  if (!element || element instanceof BorderedGroup) {
    element = new BorderedTextBox(fieldContent, options);
  }

  element.setText(fieldContent);
  element.setOptions({
    id: item.id,
    ...options,
    lockScalingY: true,
    editable: false,
    data: {
      strokeStyle: item.stroke,
      lineWidth: item.strokeWidth,
      url,
      urls,
    },
  });

  return element;
}

function renderAddressMap(
  containerEl: FieldElement,
  { item, fieldContent, url }: RenderParams,
): BorderedGroup {
  if (!containerEl || containerEl instanceof BorderedTextBox) {
    containerEl = new BorderedGroup([]);
  }

  let [imageEl] = containerEl.getObjects() as BorderedImage[];
  if (!imageEl) {
    imageEl = new BorderedImage(new Image(), {
      originX: "center",
      originY: "center",
      selectable: false,
    });
    containerEl.add(imageEl);
  }

  const { width: containerWidth = 0, height: containerHeight = 0 } = item;

  if (!fieldContent) {
    containerEl.getObjects().forEach((obj) => {
      (containerEl as fabric.Group).removeWithUpdate(obj);
    });
  } else {
    const imageUrl = makeGoogleMapsStaticMapLink(fieldContent, {
      height: containerHeight,
      width: containerWidth,
    });

    fabric.Image.fromURL(
      imageUrl,
      (img) => {
        const htmlImg = img.getElement() as HTMLImageElement;
        const isCanvasImg = htmlImg.className === "canvas-img";
        const isVisible =
          containerWidth && containerHeight && htmlImg.width && htmlImg.height;

        if (isCanvasImg && isVisible) {
          imageEl.setElement(htmlImg, {
            width: htmlImg.width,
            height: htmlImg.height,
          });
        }

        containerEl.dirty = true;
        containerEl.canvas?.requestRenderAll();
        imageEl.setCoords();
      },
      { crossOrigin: "anonymous" },
    );
  }

  // Set group/container options
  containerEl.setOptions({
    id: item.id,
    left: item.left,
    top: item.top,
    width: containerWidth,
    height: containerHeight,
    angle: item.angle,
    editable: false,
    data: {
      url,
    },
    clipPath: makeGroupClipPath(containerWidth, containerHeight),
  });

  return containerEl;
}
